'use client'

import { Check, ChevronDown, Info } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'

import { fieldIconClassName, fieldVariants } from '@/components/ui/field-variants'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { FormFlagChip } from '@/features/order/order-form/components/widget/order-form-fields'
import { useOrderFormCaps } from '@/features/order/order-form/hooks/use-order-form-caps'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import {
  offersPostOnly,
  offersReduceOnly,
  offersTif,
  reconcileOrderFlags,
} from '@/lib/configs/venue-capabilities'
import { cn } from '@/lib/utils'
import { useOrderFormCollapsibleConfig } from '@/service/ui-state.hook'
import { TimeInForce } from '@/types/generated/common/trading'

const TIME_IN_FORCE_OPTIONS = [
  { value: TimeInForce.TIF_GTC, label: 'GTC', descriptionKey: 'timeInForceDescriptions.gtc' },
  { value: TimeInForce.TIF_IOC, label: 'IOC', descriptionKey: 'timeInForceDescriptions.ioc' },
  { value: TimeInForce.TIF_FOK, label: 'FOK', descriptionKey: 'timeInForceDescriptions.fok' },
  { value: TimeInForce.TIF_GTD, label: 'GTD', descriptionKey: 'timeInForceDescriptions.gtd' },
] as const

export function OrderExecutionParams() {
  const t = useTranslations('orderForm')
  const {
    form,
    cardId,
    isMarketOrder,
    isLimitChaseMode,
    isTwapMode,
    isAdvancedTwapMode,
    isScaleMode,
  } = useUnifiedOrderFormContext()
  const { config, updateConfig } = useOrderFormCollapsibleConfig(cardId)
  const [tifOpen, setTifOpen] = useState(false)
  // The caps of the venues this ticket's rows submit to (every row must support an option),
  // not the global exchangeAtom nor another widget's tab.
  // Shape-specific rules (Derive reduce-only) must hold for every row, including mixed venues.
  const { caps, rowVenues } = useOrderFormCaps(cardId)
  // Hide TIF values the venue rejects or silently coerces (e.g. Bybit GTD → GTC).
  const timeInForceOptions = TIME_IN_FORCE_OPTIONS.filter((opt) => offersTif(caps, opt.value))

  const timeInForce = useWatch({ control: form.control, name: 'timeInForce' })
  const selectedTimeInForce =
    timeInForceOptions.find((option) => option.value === timeInForce) ?? timeInForceOptions[0]
  const reduceOnly = useWatch({ control: form.control, name: 'reduceOnly' })
  const postOnly = useWatch({ control: form.control, name: 'postOnly' })

  // Scale children always rest GTC, whatever TIF the hidden picker holds.
  const effectiveTif = isScaleMode ? TimeInForce.TIF_GTC : (timeInForce ?? TimeInForce.TIF_GTC)
  const reduceOnlyAllowed = offersReduceOnly(caps, rowVenues, {
    isMarketOrder,
    timeInForce: effectiveTif,
  })
  const postOnlyOffered = offersPostOnly(caps, { isMarketOrder, timeInForce: effectiveTif })

  const setReduceOnly = (next: boolean) => {
    form.setValue('reduceOnly', next)
    updateConfig({ reduceOnly: next ? 'true' : 'false' })
  }

  const setPostOnly = (next: boolean) => {
    form.setValue('postOnly', next)
    updateConfig({ postOnly: next ? 'true' : 'false' })
  }

  const handleTimeInForce = (val: TimeInForce) => {
    form.setValue('timeInForce', val)
    updateConfig({ timeInForce: val })
    setTifOpen(false)
  }

  // Sync config to form. The form values always follow the current caps (fallback included):
  // a flag whose chip is hidden must never be submitted. Only the persisted config waits for
  // server caps (nothing here writes it), so a saved choice returns when the chip does.
  useEffect(() => {
    const next = reconcileOrderFlags(caps, rowVenues, {
      isMarketOrder,
      isScaleMode,
      timeInForce: (config.timeInForce as TimeInForce) || TimeInForce.TIF_GTC,
      reduceOnly: config.reduceOnly === 'true',
      postOnly: config.postOnly === 'true',
    })
    form.setValue('timeInForce', next.timeInForce)
    form.setValue('reduceOnly', next.reduceOnly)
    form.setValue('postOnly', next.postOnly)
  }, [
    config.timeInForce,
    config.reduceOnly,
    config.postOnly,
    form,
    caps,
    rowVenues,
    isMarketOrder,
    isScaleMode,
  ])

  if (isTwapMode || isLimitChaseMode || isAdvancedTwapMode) return null

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex h-8 items-center gap-3">
        {/* Hidden where the venue has no reduce-only (Upbit, Toss); on Derive a resting order
            rejects it, so the chip stays but is disabled (spec §3). */}
        {caps.reduceOnly && (
          <FormFlagChip
            pressed={!!reduceOnly}
            onPressedChange={setReduceOnly}
            disabled={!reduceOnlyAllowed}
            title={!reduceOnlyAllowed ? t('reduceOnlyDisabledTitle') : undefined}
          >
            {t('reduce')}
          </FormFlagChip>
        )}

        {postOnlyOffered && (
          <FormFlagChip pressed={!!postOnly} onPressedChange={setPostOnly}>
            {t('post')}
          </FormFlagChip>
        )}
      </div>

      {!isMarketOrder && !isScaleMode && (
        <div className="min-w-24">
          <Popover open={tifOpen} onOpenChange={setTifOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(fieldVariants({ size: 'sm' }), 'group rounded-sm')}
              >
                <span className="flex items-center gap-1.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        aria-label={t('timeInForceInfo', {
                          option: selectedTimeInForce?.label ?? 'GTC',
                        })}
                        className="cursor-help"
                      >
                        <Info className="size-3" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64 text-xs leading-snug">
                      {selectedTimeInForce
                        ? t(selectedTimeInForce.descriptionKey)
                        : t('timeInForceDescriptions.gtc')}
                    </TooltipContent>
                  </Tooltip>
                  <span className="uppercase">{selectedTimeInForce?.label ?? 'GTC'}</span>
                </span>
                <ChevronDown className={fieldIconClassName} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="overflow-hidden p-0"
              style={{ width: 'var(--radix-popover-trigger-width)' }}
            >
              {timeInForceOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleTimeInForce(opt.value)}
                  className={cn(
                    'hover:bg-foreground/10 flex h-8 w-full cursor-pointer items-center gap-1.5 px-2.5 text-xs tracking-tight transition-colors',
                    timeInForce === opt.value && 'bg-foreground/5'
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        aria-label={t('timeInForceInfo', { option: opt.label })}
                        className="cursor-help"
                      >
                        <Info className="size-3" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64 text-xs leading-snug">
                      {t(opt.descriptionKey)}
                    </TooltipContent>
                  </Tooltip>
                  {opt.label}
                  {timeInForce === opt.value && (
                    <Check className="text-foreground ml-auto size-3" />
                  )}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  )
}
